import React from 'react';
import { motion } from 'framer-motion';
import { FaPlay, FaUsers, FaArrowRight } from 'react-icons/fa';

const Hero = ({ containerVariants, itemVariants }) => {
    return (
        <section className="relative min-h-[100vh] flex items-center justify-center pt-20 overflow-hidden">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 z-0">
                <motion.div
                    animate={{
                        scale: [1, 1.2, 1],
                        x: [0, 50, 0],
                        y: [0, 30, 0]
                    }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-blue-600/10 blur-[150px] rounded-full"
                ></motion.div>
                <motion.div
                    animate={{
                        scale: [1.2, 1, 1.2],
                        x: [0, -40, 0],
                        y: [0, -20, 0]
                    }}
                    transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                    className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-indigo-600/5 blur-[120px] rounded-full"
                ></motion.div>

                {/* Subtle Grid Pattern */}
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-50 contrast-150"></div>
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a]/50 to-[#0a0a0a]"></div>
            </div>

            <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="relative z-10 max-w-7xl mx-auto px-6 text-center"
            >
                <motion.div
                    variants={itemVariants}
                    className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white/[0.03] border border-white/[0.08] backdrop-blur-md text-blue-400 text-[10px] font-black tracking-[0.2em] uppercase mb-10 shadow-xl"
                >
                    <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.8)]"></span>
                    </span>
                    Premium Mathematics Education
                </motion.div>

                <motion.h1
                    variants={itemVariants}
                    className="text-4xl md:text-6xl font-black leading-[1.1] tracking-tighter mb-8"
                >
                    MASTERY <br />
                    <span className="text-gradient">THROUGH LOGIC</span>
                </motion.h1>

                <motion.p
                    variants={itemVariants}
                    className="text-base md:text-lg text-neutral-400 max-w-2xl mx-auto mb-12 leading-relaxed font-medium"
                >
                    We don't teach you to solve problems. We teach you to <span className="text-white italic">understand</span> the language of mathematics.
                </motion.p>

                <motion.div
                    variants={itemVariants}
                    className="flex flex-col sm:flex-row gap-6 justify-center items-center"
                >
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full sm:w-auto px-12 py-6 bg-blue-600 hover:bg-blue-700 text-white font-black text-sm rounded-[1.5rem] transition-all shadow-[0_20px_40px_rgba(37,99,235,0.25)] flex items-center justify-center gap-3"
                    >
                        Start Your Journey <FaArrowRight className="text-xs" />
                    </motion.button>
                    <motion.button
                        whileHover={{ scale: 1.02, backgroundColor: 'rgba(255,255,255,0.08)' }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full sm:w-auto px-12 py-6 bg-white/[0.03] border border-white/10 backdrop-blur-xl text-white font-black text-sm rounded-[1.5rem] transition-all flex items-center justify-center gap-3"
                    >
                        <FaPlay className="text-[10px] text-blue-500" /> Watch Trailer
                    </motion.button>
                </motion.div>
            </motion.div>

            {/* Floating Decorative Elements */}
            <motion.div
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute right-[10%] top-[40%] hidden lg:block"
            >
                <div className="glass-card p-6 border-blue-500/20 shadow-blue-500/10 rotate-3">
                    <div className="text-blue-500 font-black text-4xl mb-1">98%</div>
                    <div className="text-[10px] text-neutral-500 uppercase font-black tracking-widest">Success Rate</div>
                </div>
            </motion.div>

            <motion.div
                animate={{ y: [0, 20, 0] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute left-[10%] bottom-[20%] hidden lg:block"
            >
                <div className="glass-card p-6 border-indigo-500/20 shadow-indigo-500/10 -rotate-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center"><FaUsers /></div>
                        <div>
                            <div className="text-white font-black text-xl mb-0">10,000+</div>
                            <div className="text-[8px] text-neutral-500 uppercase font-black tracking-widest">Active Learners</div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </section>
    );
};

export default Hero;
